import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

/* Scheduler filter: checkbox multi-select (providers, visit types), like the
   dashboard's provider filter. `selected` is null for "all" or the list of
   picked values; filtering is live and Done only closes. Each option shows
   how many appointments it has in the period on screen. */
const MultiFilter = ({ icon, caption, allLabel, noun, options, selected, setSelected }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const values = options.map((o) => o.value);
  const isOn = (v) => selected === null || selected.includes(v);
  const allOn = selected === null;
  const toggle = (v) =>
    setSelected((prev) => {
      const current = prev === null ? values : prev;
      const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
      // every option ticked again reads as "all" (so new ones are included)
      return values.every((x) => next.includes(x)) ? null : next;
    });

  const label =
    selected === null
      ? "All"
      : selected.length === 0
        ? "None"
        : selected.length === 1
          ? options.find((o) => o.value === selected[0])?.label || selected[0]
          : `${selected.length} ${noun}`;

  return (
    <div className="bmsc-mf" ref={ref}>
      <button
        type="button"
        className={`bmsc-filter${selected !== null ? " active" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
        <span className="bmsc-filter-k">{caption}</span>
        <span className="bmsc-filter-v">{label}</span>
        <ChevronDown className="bmsc-filter-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="bmsc-pop" role="group" aria-label={caption}>
          <button
            type="button"
            role="checkbox"
            aria-checked={allOn ? "true" : selected.length ? "mixed" : "false"}
            className="bmsc-opt all"
            onClick={() => setSelected(allOn ? [] : null)}
          >
            <span className={`bmsc-box${allOn ? " on" : selected.length ? " some" : ""}`}>
              {allOn ? <Check /> : selected.length ? <span className="bmsc-dash" /> : null}
            </span>
            <span className="bmsc-opt-label">{allLabel}</span>
          </button>
          <div className="bmsc-pop-list">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="checkbox"
                aria-checked={isOn(o.value)}
                className="bmsc-opt"
                onClick={() => toggle(o.value)}
                title={o.label}
              >
                <span className={`bmsc-box${isOn(o.value) ? " on" : ""}`}>{isOn(o.value) && <Check />}</span>
                {o.dot && <span className="bmsc-opt-dot" style={{ background: o.dot }} aria-hidden="true" />}
                <span className="bmsc-opt-label">{o.label}</span>
                <span className="bmsc-opt-n">{o.count}</span>
              </button>
            ))}
            {!options.length && <div className="bmsc-pop-empty">Nothing to filter yet</div>}
          </div>
          <div className="bmsc-pop-foot">
            {/* Clears the ticks. It used to mean "back to all", which left it
                disabled in the one state it was reached from - everything
                ticked by default. */}
            <button
              type="button"
              className="bmsc-pop-clear"
              onClick={() => setSelected([])}
              disabled={selected !== null && selected.length === 0}
            >
              Clear all
            </button>
            <button type="button" className="bmsc-pop-done" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiFilter;
